import { Role } from '@/acl'
import {
  AllowedRoleTuple,
  getAllowedRolesForPath,
} from '@/acl/menu-allowed-roles'
import { MyRouterContext } from '@/api/auth'
import { menus } from '@/components/layout/menu'
import { redirect } from '@tanstack/react-router'

/**
 * =====================================================================
 * ROUTE GUARDS - EXCEL ACL MATRIX INTEGRATION
 * =====================================================================
 *
 * This module uses the Excel ACL matrix as the single source of truth
 * for route permissions. The guards now read from `allowedRoles` which
 * is populated from `menu-allowed-roles.json` (generated from Excel).
 *
 * 📚 For migration details and usage examples, see:
 *    docs/route-guards-excel-acl-migration.md
 *
 * 🔄 Data Flow:
 *    Excel ACL Matrix → JSON → menu.tsx (allowedRoles) → Route Guards
 *
 * ⚙️ To update permissions:
 *    1. Edit Excel ACL matrix
 *    2. Run: node scripts/generate-menu-allowed-roles-json-from-excel.mjs
 *    3. Commit and deploy
 *
 * =====================================================================
 */

/**
 * Finds the allowed roles for a given route path by searching through the menu configuration.
 * Uses the `allowedRoles` property which is populated from the Excel ACL matrix via JSON.
 * This ensures the Excel matrix is the single source of truth for permissions.
 *
 * @param path - The route path to search for (e.g., '/production/prd1110')
 * @returns Array of allowed role tuples [Role, 'R'|'W'], or undefined if no roles specified for this path
 *
 * @example
 * const allowedRoles = getMenuAllowedRolesByPath('/production/prd1110')
 * // Returns: [[Role.ADMIN, 'W'], [Role.PRODUCTION_OFFICER, 'R'], ...]
 */
export function getMenuAllowedRolesByPath(
  path: string
): AllowedRoleTuple[] | undefined {
  for (const group of menus) {
    for (const section of group.sections) {
      for (const item of section.menus) {
        if (item.path === path) {
          return getAllowedRolesForPath(path)
        }
      }
    }
  }
  return undefined
}

/**
 * Extracts just the roles (without access level) from allowedRoles tuples.
 *
 * @param allowedRoles - Array of [Role, 'R'|'W'] tuples
 * @returns Array of Role values
 */
export function extractRoles(allowedRoles: AllowedRoleTuple[]): Role[] {
  return allowedRoles.map(([role]) => role)
}

export type RequiredAccess = 'R' | 'W'

/**
 * Checks if user has required roles for a route path based on menu configuration.
 * Uses `allowedRoles` from Excel ACL matrix (via JSON) as the source of truth.
 * Throws redirect to /no-permission if unauthorized.
 *
 * Use this INSIDE existing beforeLoad functions to add permission checks.
 *
 * @param routePath - The route path to check permissions for (e.g., '/warehouse/inv3010')
 * @param context - The router context containing auth information
 * @throws {redirect} Redirects to /no-permission if user lacks required roles
 *
 * @example
 * // Inside existing beforeLoad:
 * beforeLoad: async ({ context, params }) => {
 *   // Check permissions FIRST
 *   checkMenuRoles('/warehouse/inv3010', context)
 *
 *   // Then do existing logic (e.g., check document status)
 *   const invData = await context.queryClient.ensureQueryData(...)
 *   if (invData.status !== 1) {
 *     throw redirect({ to: '../../${params.id}' })
 *   }
 * }
 */
export function checkMenuRoles(
  routePath: string,
  context: MyRouterContext,
  requiredAccess: RequiredAccess = 'R'
): void {
  const allowedRoles = getMenuAllowedRolesByPath(routePath)

  // If allowedRoles are defined in menu (from Excel ACL) and user doesn't have required role, deny access
  if (allowedRoles && allowedRoles.length > 0) {
    const userRoles = context.auth?.user?.roleNames ?? []
    const hasSuperAccess = context.auth?.hasRequiredRole([]) ?? false
    const hasAccess =
      hasSuperAccess ||
      allowedRoles.some(([role, access]) => {
        if (!userRoles.includes(role)) return false

        return requiredAccess === 'R'
          ? access === 'R' || access === 'W'
          : access === 'W'
      })

    if (!hasAccess) {
      throw redirect({
        to: '/no-permission',
      })
    }
  }

  // If no allowedRoles defined in menu, allow access to all authenticated users
  // (authentication is already checked by _auth route)
}

/**
 * Creates a standalone beforeLoad guard for routes WITHOUT existing beforeLoad logic.
 * Automatically extracts allowed roles from Excel ACL matrix (via menu.tsx allowedRoles).
 *
 * If no roles are found in the ACL matrix, the route is accessible to all authenticated users.
 * If roles are found but user doesn't have them, redirects to /no-permission page.
 *
 * @param routePath - The route path to check permissions for (e.g., '/production/prd1110')
 * @returns A beforeLoad function that can be used in route configuration
 *
 * @example
 * // For routes WITHOUT existing beforeLoad:
 * export const Route = createFileRoute('/_auth/production/prd1110/')({
 *   beforeLoad: requireMenuRoles('/production/prd1110'),
 *   component: Listing,
 * })
 *
 * @see checkMenuRoles - Use this for routes WITH existing beforeLoad logic
 */
export function requireMenuRoles(
  routePath: string,
  requiredAccess: RequiredAccess = 'R'
) {
  return ({ context }: { context: MyRouterContext }) => {
    checkMenuRoles(routePath, context, requiredAccess)
  }
}
